import lockedSelectedStyles from './selected-styles.json';
import { getStyleById, PRODUCT_CATEGORY_MAP, ProductKey } from './style-definitions';

export interface SelectedStyles {
  projectManagerApp: string;
  checkInPlatform: string;
}

export const STYLE_SELECTION_STORAGE_KEY = 'design-intelligence.selected-styles.v1';

const HARD_FALLBACK_SELECTIONS: SelectedStyles = {
  projectManagerApp: 'data-grid-focus',
  checkInPlatform: 'field-clear',
};

function isBrowser() {
  return typeof window !== 'undefined';
}

function getLockedSelectionsSafe(): SelectedStyles {
  const locked = lockedSelectedStyles as Partial<SelectedStyles>;

  return {
    projectManagerApp: isValidSelection('projectManagerApp', locked.projectManagerApp ?? '')
      ? (locked.projectManagerApp as string)
      : HARD_FALLBACK_SELECTIONS.projectManagerApp,
    checkInPlatform: isValidSelection('checkInPlatform', locked.checkInPlatform ?? '')
      ? (locked.checkInPlatform as string)
      : HARD_FALLBACK_SELECTIONS.checkInPlatform,
  };
}

export const LOCKED_SELECTED_STYLES = getLockedSelectionsSafe();

export function isValidSelection(product: ProductKey, styleId: string): boolean {
  const style = getStyleById(styleId);
  return Boolean(style && style.category === PRODUCT_CATEGORY_MAP[product]);
}

export function normalizeSelectedStyles(input?: Partial<SelectedStyles> | null): SelectedStyles {
  const lockedSelections = getLockedSelectionsSafe();
  const rawProjectManagerApp = input?.projectManagerApp ?? lockedSelections.projectManagerApp;
  const rawCheckInPlatform = input?.checkInPlatform ?? lockedSelections.checkInPlatform;

  return {
    projectManagerApp: isValidSelection('projectManagerApp', rawProjectManagerApp)
      ? rawProjectManagerApp
      : HARD_FALLBACK_SELECTIONS.projectManagerApp,
    checkInPlatform: isValidSelection('checkInPlatform', rawCheckInPlatform)
      ? rawCheckInPlatform
      : HARD_FALLBACK_SELECTIONS.checkInPlatform,
  };
}

export function readPersistedSelectedStyles(): SelectedStyles {
  if (!isBrowser()) {
    return LOCKED_SELECTED_STYLES;
  }

  try {
    const raw = window.localStorage.getItem(STYLE_SELECTION_STORAGE_KEY);
    if (!raw) {
      return LOCKED_SELECTED_STYLES;
    }

    return normalizeSelectedStyles(JSON.parse(raw) as Partial<SelectedStyles>);
  } catch {
    return LOCKED_SELECTED_STYLES;
  }
}

export function persistSelectedStyles(selectedStyles: SelectedStyles) {
  if (!isBrowser()) {
    return;
  }

  window.localStorage.setItem(
    STYLE_SELECTION_STORAGE_KEY,
    JSON.stringify(selectedStyles, null, 2),
  );
}

export function withSelectedStyle(
  selectedStyles: SelectedStyles,
  product: ProductKey,
  styleId: string,
): SelectedStyles {
  if (!isValidSelection(product, styleId)) {
    return selectedStyles;
  }

  return {
    ...selectedStyles,
    [product]: styleId,
  };
}
